import { useMemo } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface Props {
  allData: any[];
}

export default function SystemHealthView({ allData }: Props) {
  const stats = useMemo(() => {
    const total       = allData.length;
    const withCoords  = allData.filter((d: any) => d.latitude != null && d.longitude != null).length;
    const withSpecies = allData.filter((d: any) => d.species).length;
    const withAccused = allData.filter((d: any) => d.nameOfAccused).length;
    const withCharge  = allData.filter((d: any) => d.charge1).length;
    const coordPct    = total ? Math.round((withCoords  / total) * 100) : 0;
    const speciesPct  = total ? Math.round((withSpecies / total) * 100) : 0;
    const accusedPct  = total ? Math.round((withAccused / total) * 100) : 0;
    const chargePct   = total ? Math.round((withCharge  / total) * 100) : 0;
    const overallHealth = Math.round((coordPct + speciesPct + accusedPct + chargePct) / 4);
    return { total, withCoords, withSpecies, withAccused, withCharge, coordPct, speciesPct, accusedPct, chargePct, overallHealth };
  }, [allData]);

  const healthColor = stats.overallHealth >= 80 ? '#4ade80' : stats.overallHealth >= 60 ? '#fbbf24' : '#f87171';

  const fields = [
    { label: 'Coordinates',   pct: stats.coordPct,   count: stats.withCoords,  of: stats.total, help: 'Required for map visualisation' },
    { label: 'Species',       pct: stats.speciesPct,  count: stats.withSpecies, of: stats.total, help: 'Required for species analytics' },
    { label: 'Accused Name',  pct: stats.accusedPct,  count: stats.withAccused, of: stats.total, help: 'Required for offender intelligence' },
    { label: 'Charge Filed',  pct: stats.chargePct,   count: stats.withCharge,  of: stats.total, help: 'Required for judicial tracking' },
  ];

  function HealthIcon({ pct }: { pct: number }) {
    if (pct >= 80) return <CheckCircleIcon className="w-4 h-4 text-emerald-400 shrink-0" />;
    if (pct >= 50) return <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 shrink-0" />;
    return <XCircleIcon className="w-4 h-4 text-red-400 shrink-0" />;
  }

  const recentRecords = [...allData].reverse().slice(0, 8);

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>System Health</h2>
        <p className="text-white/30 text-xs mt-0.5">Dataset quality and platform metrics</p>
      </div>

      {/* Overall health card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-white/6 p-5 flex items-center gap-5 md:col-span-1" style={{ background: '#0a2e18' }}>
          <div className="relative w-16 h-16 shrink-0">
            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15" fill="none"
                stroke={healthColor}
                strokeWidth="3"
                strokeDasharray={`${(stats.overallHealth / 100) * 94.2} 94.2`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums" style={{ color: healthColor, fontFamily: 'var(--font-display)' }}>
              {stats.overallHealth}%
            </span>
          </div>
          <div>
            <p className="text-white/50 text-xs">Overall Health</p>
            <p className="text-white/85 text-base font-semibold mt-0.5" style={{ fontFamily: 'var(--font-display)' }}>
              {stats.overallHealth >= 80 ? 'Excellent' : stats.overallHealth >= 60 ? 'Moderate' : 'Needs Attention'}
            </p>
            <p className="text-white/30 text-xs mt-0.5">{stats.total} total records</p>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-4 md:col-span-2">
          {[
            { label: 'Total Records',       value: stats.total,                        color: 'text-white' },
            { label: 'With Coordinates',    value: `${stats.withCoords} (${stats.coordPct}%)`,  color: stats.coordPct < 50 ? 'text-red-400' : 'text-green-400' },
            { label: 'Missing Coords',      value: stats.total - stats.withCoords,     color: stats.total - stats.withCoords > 0 ? 'text-amber-400' : 'text-emerald-400' },
            { label: 'Data Completeness',   value: `${stats.overallHealth}%`,          color: healthColor === '#4ade80' ? 'text-emerald-400' : 'text-amber-400' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-white/6 px-4 py-3 flex flex-col gap-1" style={{ background: '#0a2e18' }}>
              <span className="text-white/30 text-[10px]">{s.label}</span>
              <span className={`text-xl font-bold tabular-nums ${s.color}`} style={{ fontFamily: 'var(--font-display)' }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Field completeness */}
      <div className="rounded-xl border border-white/6 p-5 mb-5" style={{ background: '#0a2e18' }}>
        <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Field Completeness</p>
        <div className="flex flex-col gap-4">
          {fields.map((f) => (
            <div key={f.label}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <HealthIcon pct={f.pct} />
                  <span className="text-white/70 text-xs">{f.label}</span>
                  <span className="text-white/25 text-[10px]">— {f.help}</span>
                </div>
                <span className="text-white/50 text-xs tabular-nums">{f.count}/{f.of}</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${f.pct}%`,
                    background: f.pct >= 80 ? '#4ade80' : f.pct >= 50 ? '#fbbf24' : '#f87171',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent records */}
      <div className="rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
        <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Most Recent Records</p>
        <div className="flex flex-col gap-0">
          {recentRecords.map((r: any, i: number) => (
            <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-white/20 text-[10px] tabular-nums w-4">{i + 1}</span>
                <div className="min-w-0">
                  <p className="text-white/70 text-xs truncate">{r.species || '—'}</p>
                  <p className="text-white/28 text-[10px] truncate">{r.location || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {r.latitude != null
                  ? <span className="text-emerald-400 text-[9px]">&#10003; coords</span>
                  : <span className="text-amber-400 text-[9px]">&#9888; no coords</span>}
                <span className="text-white/25 text-[10px]">{r.dateOfSeizure || '—'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
